import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import dataService from '../../services/dataService';
import { useI18n } from '../../features/i18n';
import { useDocumentTitle } from '../shell/DocumentTitle';
import { Skeleton, SkeletonRow } from '../shell/Skeleton';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/dish/:restaurantId/:dishId — a full-page deep dive on a single
 * menu item. Stitches together four guest-facing concerns:
 *
 *   1. Dish identity (photo, description, price, dietary chips)
 *   2. Allergen severity matrix — same per-allergen palette as the rest of
 *      the unified app (amber → orange → red → black), grouped by
 *      "contains / prepared with / may contain"
 *   3. Suggested pairings — other items the restaurant's menu knows about,
 *      ranked by a cheap "same meal" heuristic
 *   4. Similar dishes — items in the same category, minus the one we're on
 *
 * The page is additive — no other routes change. Menu Manager and
 * RestaurantDetail can deep-link here; the "back" button tries browser
 * history first, then falls back to the restaurant detail.
 */

const EASE = [0.22, 1, 0.36, 1];

// Severity inference — a lightweight classifier until the backend returns
// per-allergen severity metadata. `contains` = listed allergen. `mayContain`
// = in removable_ingredients or notes. `preparedWith` = in prep_methods or
// description keywords. Prefer upstream metadata when present.
//
// Labels are NOT stored here — they're resolved at render via
// SEVERITY_LABEL_KEY so changing language rebuilds them on the fly.
const ALLERGEN_TONE = {
  default:    { key: 'contains',   bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',    solid: '#B91C1C' },
  prepared:   { key: 'prepared',   bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)',  solid: '#D97706' },
  mayContain: { key: 'mayContain', bg: '#FEF3C7',                fg: '#92400E',                solid: '#F59E0B' },
  critical:   { key: 'critical',   bg: '#1F2937',                fg: '#fff',                   solid: '#1F2937' },
};
const SEVERITY_LABEL_KEY = {
  contains:   'dish.sev_contains',
  prepared:   'dish.sev_prepared',
  mayContain: 'dish.sev_may_contain',
  critical:   'dish.sev_critical',
};

export default function UnifiedDishDetail() {
  const { restaurantId, dishId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const categoryId = params.get('category') || null;

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.getRestaurant(restaurantId);
        if (!cancelled) setRestaurant(data);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load dish.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  // Flatten all items so we can deep-link via item.id even when the
  // category isn't known
  const { dish, category, allItems } = useMemo(() => findDishInRestaurant(restaurant, dishId, categoryId), [restaurant, dishId, categoryId]);

  // Tab title = "{dish} · {restaurant}" when both are known — helps when
  // a diner has multiple dish tabs open to compare.
  useDocumentTitle(
    dish?.name && restaurant?.name
      ? `${dish.name} · ${restaurant.name}`
      : dish?.name || restaurant?.name || null
  );
  const similar = useMemo(() => pickSimilar(category, dish), [category, dish]);
  const pairings = useMemo(() => pickPairings(allItems, dish), [allItems, dish]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else if (restaurantId) navigate(`/unified/restaurant/${restaurantId}`);
    else navigate('/unified/discover');
  };

  if (loading) return <AppShell backdrop><CenterLoader t={t} /></AppShell>;
  if (!restaurant || !dish) return <AppShell backdrop><NotFound t={t} onBack={goBack} /></AppShell>;

  const price = formatPrice(dish);
  const photo = dish.photo?.dataUrl || dish.photo_url || null;

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1020, margin: '0 auto', padding: '22px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Back + crumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 12.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
          <button type="button" onClick={goBack}
                  style={{
                    padding: '7px 14px', borderRadius: 999,
                    background: '#fff', color: 'var(--cwm-blue-deep)',
                    border: '1.5px solid var(--cwm-blue-light)',
                    fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
            <ArrowIcon size={11} dir="left" /> {t('dish.back')}
          </button>
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <a href={`/unified/restaurant/${restaurantId}`} style={{ color: 'var(--cwm-blue-deep)', fontWeight: 800, textDecoration: 'none' }}>
              {restaurant.name}
            </a>
            {category?.name && (<>
              <span style={{ color: 'var(--cwm-gray-300)' }}>·</span>
              <span>{category.name}</span>
            </>)}
          </span>
        </div>

        {error && (
          <div className="cwm-sheet" style={{ padding: 12, marginBottom: 14, background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)', fontSize: 13, fontWeight: 800 }}>
            {error}
          </div>
        )}

        {/* Hero card — photo + identity */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.48, ease: EASE }}
          className="cwm-sheet" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 0 }} className="cwm-dish-hero">
            <style>{`@media (max-width: 760px) { .cwm-dish-hero { grid-template-columns: 1fr !important; } }`}</style>
            <div style={{
              minHeight: 280,
              background: photo ? `url(${photo}) center/cover no-repeat` : 'linear-gradient(160deg, var(--cwm-blue-pale), #fff)',
              position: 'relative',
            }}>
              {!photo && (
                <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--cwm-blue-deep)', opacity: 0.32 }}>
                  <PlateIcon size={72} />
                </div>
              )}
              {dish.needs_review && (
                <span style={{
                  position: 'absolute', top: 12, left: 12,
                  padding: '4px 10px', borderRadius: 999,
                  background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
                  fontSize: 10, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                  {t('dish.needs_review')}
                </span>
              )}
            </div>

            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div className="cwm-eyebrow">{category?.name || t('dish.menu_fallback')}</div>
                <h1 style={{ margin: '4px 0 8px', fontSize: 28, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.02em', lineHeight: 1.12 }}>
                  {dish.name}
                </h1>
                {dish.description && (
                  <p style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.55 }}>
                    {dish.description}
                  </p>
                )}

                {/* Dietary + price */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
                  {(dish.dietary_tags || []).map((t) => (
                    <span key={t} style={{
                      padding: '3px 10px', borderRadius: 999,
                      background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
                      border: '1px solid #A6E8C2',
                      fontSize: 11.5, fontWeight: 800,
                    }}>✓ {t}</span>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.02em' }}>
                  {price}
                </div>
                <div style={{ display: 'inline-flex', gap: 6 }}>
                  <button type="button" onClick={() => navigate('/unified/chat', { state: { restaurantId, restaurantName: restaurant.name, promptDish: dish.name } })}
                          className="cwm-btn" style={{ padding: '10px 18px', fontSize: 13 }}>
                    <ChatIcon size={13} /> {t('dish.ask_about_this')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Allergen severity matrix */}
        <AllergenMatrix t={t} dish={dish} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }} className="cwm-dish-grid">
          <style>{`@media (max-width: 760px) { .cwm-dish-grid { grid-template-columns: 1fr !important; } }`}</style>

          {/* Modifier groups / removable ingredients / prep */}
          <PrepDetails t={t} dish={dish} />

          {/* Pairings */}
          <PairingsList t={t} pairings={pairings} restaurantId={restaurantId} />
        </div>

        {/* Similar dishes */}
        <SimilarDishes t={t} similar={similar} category={category} restaurantId={restaurantId} />

        {/* CTAs */}
        <div className="cwm-sheet" style={{ marginTop: 16, padding: 18, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(160deg, #fff, var(--cwm-blue-pale))' }}>
          <div>
            <div className="cwm-eyebrow">{t('dish.next_step_eyebrow')}</div>
            <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>
              {t('dish.next_step_title')}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cwm-gray-700)', marginTop: 2 }}>
              {t('dish.next_step_body', { name: restaurant.name })}
            </div>
          </div>
          <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => navigate('/unified/safety-signals', { state: { restaurantId, dishName: dish.name } })}
                    className="cwm-btn-secondary" style={{ padding: '10px 16px', fontSize: 12.5 }}>
              <ShieldIcon size={12} /> {t('dish.share_safety_cta')}
            </button>
            <button type="button" onClick={() => navigate('/unified/chat', { state: { restaurantId, restaurantName: restaurant.name, promptDish: dish.name } })}
                    className="cwm-btn" style={{ padding: '10px 16px', fontSize: 12.5 }}>
              <ChatIcon size={12} /> {t('dish.chat_about', { name: dish.name.split(' ').slice(0, 2).join(' ') })}
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// ============================================================
// ALLERGEN MATRIX
// ============================================================

function AllergenMatrix({ t, dish }) {
  const allergens = useMemo(() => inferAllergenSeverity(dish), [dish]);

  if (allergens.length === 0) {
    return (
      <div className="cwm-sheet" style={{ padding: 18 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)' }}>{t('dish.allergen_eyebrow')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 12,
            background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
            display: 'grid', placeItems: 'center', border: '1.5px solid #A6E8C2',
          }}>
            <CheckIcon size={16} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{t('dish.allergen_clear_title')}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cwm-gray-500)' }}>
              {t('dish.allergen_clear_body')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Group by tone
  const groups = {
    contains:   allergens.filter((a) => a.severity.key === 'contains' || a.severity.key === 'critical'),
    prepared:   allergens.filter((a) => a.severity.key === 'prepared'),
    mayContain: allergens.filter((a) => a.severity.key === 'mayContain'),
  };

  return (
    <div className="cwm-sheet" style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-red-text)' }}>{t('dish.allergen_eyebrow')}</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>
            {allergens.length === 1 ? t('dish.allergen_count_one') : t('dish.allergen_count', { count: allergens.length })}
          </div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', maxWidth: 240, textAlign: 'right' }}>
          {t('dish.allergen_hint')}
        </div>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {Object.entries(groups).map(([key, list]) => {
          if (!list.length) return null;
          const tone = ALLERGEN_TONE[key === 'contains' ? 'default' : key] || ALLERGEN_TONE.default;
          // Pull the translated group label from the severity key.
          const groupLabelKey = SEVERITY_LABEL_KEY[key] || SEVERITY_LABEL_KEY.contains;
          return (
            <div key={key} style={{ borderRadius: 14, background: tone.bg, padding: '12px 14px', border: `1.5px solid ${tone.solid}22` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: 999, background: tone.solid }}/>
                <div style={{ fontSize: 11, fontWeight: 900, color: tone.fg, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {t(groupLabelKey)} ({list.length})
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {list.map((a) => (
                  <span key={a.name} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', borderRadius: 999,
                    background: a.severity.key === 'critical' ? '#1F2937' : '#fff',
                    color: a.severity.key === 'critical' ? '#fff' : tone.fg,
                    border: `1.5px solid ${tone.solid}`,
                    fontSize: 12, fontWeight: 800,
                  }}>
                    {a.severity.key === 'critical' ? <AlertIcon size={10} /> : <WarnIcon size={10} />}
                    {a.name}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// PREP / MODIFIERS
// ============================================================

function PrepDetails({ t, dish }) {
  const prep = dish.prep_methods || [];
  const removable = dish.removable_ingredients || [];
  const mods = dish.modifier_groups || [];
  const empty = prep.length === 0 && removable.length === 0 && mods.length === 0;

  return (
    <div className="cwm-sheet" style={{ padding: 18 }}>
      <div className="cwm-eyebrow">{t('dish.prep_eyebrow')}</div>
      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 12 }}>
        {t('dish.prep_title')}
      </div>

      {empty && (
        <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-500)', fontWeight: 600 }}>
          {t('dish.prep_empty')}
        </div>
      )}

      {prep.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="cwm-eyebrow" style={{ fontSize: 9.5, color: 'var(--cwm-blue-deep)', marginBottom: 6 }}>{t('dish.prep_methods_eyebrow')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {prep.map((p) => (
              <span key={p} style={{
                padding: '4px 10px', borderRadius: 999,
                background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                border: '1px solid var(--cwm-blue-light)',
                fontSize: 11.5, fontWeight: 800,
              }}>{p}</span>
            ))}
          </div>
        </div>
      )}

      {removable.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="cwm-eyebrow" style={{ fontSize: 9.5, color: 'var(--cwm-green-text)', marginBottom: 6 }}>{t('dish.prep_removable_eyebrow')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {removable.map((r) => (
              <span key={r} style={{
                padding: '4px 10px', borderRadius: 999,
                background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
                border: '1px solid #A6E8C2',
                fontSize: 11.5, fontWeight: 800,
              }}>⊖ {r}</span>
            ))}
          </div>
        </div>
      )}

      {mods.length > 0 && (
        <div>
          <div className="cwm-eyebrow" style={{ fontSize: 9.5, color: 'var(--cwm-amber-text)', marginBottom: 6 }}>
            {t('dish.prep_mods_eyebrow')}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {mods.map((g) => (
              <div key={g.id} style={{
                padding: '8px 12px', borderRadius: 10,
                background: 'var(--cwm-gray-50)', border: '1px solid var(--cwm-gray-100)',
              }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--cwm-ink)' }}>
                  {g.name}{g.required ? ` · ${t('dish.prep_required')}` : ''}
                </div>
                {g.options?.length > 0 && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
                    {g.options.slice(0, 5).map((o) => o.name).join(' · ')}
                    {g.options.length > 5 ? '…' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PAIRINGS
// ============================================================

function PairingsList({ t, pairings, restaurantId }) {
  const navigate = useNavigate();
  // Translate the category reason labels (diner's-side hint next to each
  // pairing). Upstream `pickPairings` tags each with an English reason key.
  const reasonLabel = (reason) => {
    const map = {
      'A drink pair':   t('dish.pair_drink'),
      'To start':       t('dish.pair_start'),
      'To finish':      t('dish.pair_finish'),
      'On the side':    t('dish.pair_side'),
    };
    return map[reason] || reason;
  };
  return (
    <div className="cwm-sheet" style={{ padding: 18 }}>
      <div className="cwm-eyebrow" style={{ color: '#7C3AED' }}>{t('dish.pair_eyebrow')}</div>
      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 12 }}>
        {t('dish.pair_title')}
      </div>

      {pairings.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-500)', fontWeight: 600 }}>
          {t('dish.pair_empty')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {pairings.map((p) => (
            <button key={p.id} type="button" onClick={() => navigate(`/unified/dish/${restaurantId}/${p.id}${p.categoryId ? `?category=${p.categoryId}` : ''}`)}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px', borderRadius: 12,
                      background: '#fff', border: '1.5px solid var(--cwm-gray-100)',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                      transition: 'all 160ms ease',
                    }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: `linear-gradient(160deg, hsl(${(p.name.charCodeAt(0) * 23) % 360} 70% 85%), hsl(${(p.name.charCodeAt(0) * 23 + 30) % 360} 65% 72%))`,
                color: '#5B21B6', display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <PlateIcon size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {reasonLabel(p.reason)}
                </div>
              </div>
              <ArrowIcon size={11} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SIMILAR
// ============================================================

function SimilarDishes({ t, similar, category, restaurantId }) {
  const navigate = useNavigate();
  if (similar.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
        <div>
          <div className="cwm-eyebrow">{t('dish.similar_eyebrow_prefix')} {category?.name?.toUpperCase() || t('dish.similar_menu_fallback')}</div>
          <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, letterSpacing: '-0.01em' }}>
            {t('dish.similar_title')}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
        {similar.slice(0, 6).map((it) => (
          <motion.button
            key={it.id} type="button"
            whileHover={{ y: -2 }}
            onClick={() => navigate(`/unified/dish/${restaurantId}/${it.id}${it.categoryId ? `?category=${it.categoryId}` : ''}`)}
            className="cwm-sheet"
            style={{ padding: 0, textAlign: 'left', background: '#fff', border: 'none', cursor: 'pointer', overflow: 'hidden' }}
          >
            <div style={{
              height: 110,
              background: it.photo?.dataUrl
                ? `url(${it.photo.dataUrl}) center/cover no-repeat`
                : `linear-gradient(160deg, var(--cwm-blue-pale), #fff)`,
              borderBottom: '1px solid var(--cwm-gray-100)',
            }}/>
            <div style={{ padding: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--cwm-ink)' }}>{it.name}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
                  {(it.allergens?.length || 0) === 0
                    ? <span style={{ color: 'var(--cwm-green-text)' }}>{t('dish.similar_no_allergens')}</span>
                    : (it.allergens.length === 1
                        ? t('dish.similar_one_allergen')
                        : t('dish.similar_n_allergens', { n: it.allergens.length }))
                  }
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{formatPrice(it)}</div>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// HELPERS
// ============================================================

function findDishInRestaurant(restaurant, dishId, categoryId) {
  if (!restaurant) return { dish: null, category: null, allItems: [] };
  const menus = (restaurant.menus || []).flatMap((m) => m.menu_data?.menus || []);
  const allCats = menus.flatMap((m) => (m.categories || []));
  const allItems = allCats.flatMap((c) => (c.items || []).map((i) => ({ ...i, categoryId: c.id, categoryName: c.name })));

  let dish = null;
  let category = null;

  for (const c of allCats) {
    const match = (c.items || []).find((i) => String(i.id) === String(dishId));
    if (match) {
      dish = match;
      category = c;
      break;
    }
  }
  if (!dish && categoryId) {
    const c = allCats.find((c) => String(c.id) === String(categoryId));
    if (c) category = c;
  }
  return { dish, category, allItems };
}

function pickSimilar(category, dish) {
  if (!category?.items || !dish) return [];
  return (category.items || [])
    .filter((i) => i.id !== dish.id && i.available !== false)
    .map((i) => ({ ...i, categoryId: category.id }));
}

function pickPairings(allItems, dish) {
  if (!allItems.length || !dish) return [];
  const dishCat = (dish.categoryName || '').toLowerCase();

  // Heuristic: prefer items from different categories (so you get drink +
  // starter + main), and penalize anything that shares the current dish's
  // allergens (safer default). Cap at 4.
  const scored = allItems
    .filter((i) => i.id !== dish.id && i.available !== false)
    .map((i) => {
      let score = 0;
      const iCat = (i.categoryName || '').toLowerCase();
      if (iCat && dishCat && iCat !== dishCat) score += 5;
      if (isDrink(iCat) && !isDrink(dishCat)) score += 3;
      if (isDessert(iCat) && !isDessert(dishCat)) score += 2;
      if (isStarter(iCat) && !isStarter(dishCat)) score += 2;
      // Penalise shared allergens
      const iAll = new Set(i.allergens || []);
      const shared = (dish.allergens || []).filter((a) => iAll.has(a));
      score -= shared.length;
      return { ...i, _score: score };
    })
    .sort((a, b) => b._score - a._score)
    .slice(0, 4);

  return scored.map((i) => ({
    ...i,
    reason: isDrink(i.categoryName || '')   ? 'A drink pair'
          : isStarter(i.categoryName || '') ? 'To start'
          : isDessert(i.categoryName || '') ? 'To finish'
          :                                   'On the side',
  }));
}

function isDrink(s)   { return /drink|beverage|wine|beer|cocktail|coffee|tea/.test(s); }
function isStarter(s) { return /starter|appetizer|small|share|antipasti|salad/.test(s); }
function isDessert(s) { return /dessert|sweet|gelato|cookie|pastr/.test(s); }

function inferAllergenSeverity(dish) {
  if (!dish) return [];
  const listed = dish.allergens || [];

  // If upstream provides per-allergen severity, use that directly.
  if (Array.isArray(dish.allergen_severity) && dish.allergen_severity.length > 0) {
    return dish.allergen_severity.map((e) => ({
      name: e.name,
      severity: ALLERGEN_TONE[e.severity] || ALLERGEN_TONE.default,
    }));
  }

  const removableSet = new Set((dish.removable_ingredients || []).map(toLower));
  const prepMethodsText = (dish.prep_methods || []).join(' ').toLowerCase();
  const descriptionText = (dish.description || '').toLowerCase();
  const searchText = `${prepMethodsText} ${descriptionText}`;

  return listed.map((a) => {
    const low = toLower(a);
    // Critical if severe/anaphylaxis keywords anywhere
    if (/(peanut|tree nut|shellfish)/.test(low)) {
      return { name: a, severity: ALLERGEN_TONE.critical };
    }
    // May contain if removable
    if (removableSet.has(low)) {
      return { name: a, severity: ALLERGEN_TONE.mayContain };
    }
    // Prepared with if it shows up in prep context (e.g. "fried in peanut oil")
    if (searchText.includes(low)) {
      return { name: a, severity: ALLERGEN_TONE.prepared };
    }
    // Default: Contains
    return { name: a, severity: ALLERGEN_TONE.default };
  });
}

function toLower(s) { return String(s || '').toLowerCase(); }

function formatPrice(item) {
  if (item?.price_type === 'MP' || item?.price == null) return 'MP';
  const n = Number(item.price);
  if (!Number.isFinite(n)) return '';
  const dollars = n >= 100 ? n / 100 : n;
  return `$${dollars.toFixed(2)}`;
}

// ============================================================
// STATES
// ============================================================

function CenterLoader({ t }) {
  return (
    <div style={{ maxWidth: 1020, margin: '0 auto', padding: '22px 20px 80px' }}>
      {/* Back + crumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Skeleton h={30} w={80} radius={999} />
        <Skeleton h={14} w={180} />
      </div>
      {/* Hero card skeleton */}
      <div className="cwm-sheet" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)' }}>
          <div style={{ minHeight: 280, background: 'var(--cwm-blue-pale)' }}/>
          <div style={{ padding: 22 }}>
            <Skeleton h={10} w={80} mb={8} />
            <Skeleton h={28} w="75%" mb={10} />
            <SkeletonRow lines={3} h={12} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
              <Skeleton h={24} w={80} />
              <Skeleton h={36} w={120} radius={999} />
            </div>
          </div>
        </div>
      </div>
      {/* Allergen matrix skeleton */}
      <div className="cwm-sheet" style={{ padding: 18, marginBottom: 16 }}>
        <Skeleton h={10} w={140} mb={8} />
        <Skeleton h={16} w={200} mb={12} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[0,1,2,3].map((i) => <Skeleton key={i} h={28} w={100} radius={999} />)}
        </div>
      </div>
      <span style={{ position: 'absolute', left: -9999 }}>{t('dish.loading')}</span>
    </div>
  );
}

function NotFound({ t, onBack }) {
  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ width: 60, height: 60, margin: '0 auto 14px', borderRadius: 18, background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)', display: 'grid', placeItems: 'center' }}>
        <PlateIcon size={26} />
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 8px' }}>{t('dish.not_found_title')}</h1>
      <p style={{ fontSize: 14, color: 'var(--cwm-gray-700)', marginBottom: 18 }}>
        {t('dish.not_found_body')}
      </p>
      <button className="cwm-btn" onClick={onBack}>{t('dish.not_found_cta')}</button>
    </div>
  );
}

// ============================================================
// ICONS
// ============================================================

function PlateIcon({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="5"/></svg>; }
function ArrowIcon({ size = 12, dir = 'right' }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ transform: dir === 'left' ? 'rotate(180deg)' : 'none' }}><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
function CheckIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function ChatIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z"/></svg>; }
function ShieldIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/></svg>; }
function WarnIcon({ size = 10 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01"/><path d="M10.3 3.86L1.82 18a2 2 0 001.72 3h16.92a2 2 0 001.72-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>; }
function AlertIcon({ size = 10 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>; }
